import type { GameRunStats } from '../../../domain/gameplay/GameRunStats';
import type { LeaderboardEntry } from '../models/leaderboard-entry';
import { validateNickname } from '../models/nickname';
import type { SubmissionOutcome } from '../services/score-submission-service';

export class LeaderboardPanel {
  private readonly form: HTMLFormElement;
  private readonly input: HTMLInputElement;
  private readonly submitButton: HTMLButtonElement;
  private readonly status: HTMLElement;
  private readonly board: HTMLElement;
  private readonly list: HTMLOListElement;
  private readonly summary: HTMLElement;
  private readonly rank: HTMLElement;
  private busy = false;
  private submitted = false;
  private hasFinishedRun = false;
  private currentScoreId?: string;

  public constructor(
    root: HTMLElement,
    private readonly onSubmit: (nickname: string) => void,
  ) {
    this.form = requireElement(root, '#score-submit-form', HTMLFormElement);
    this.input = requireElement(root, '#leaderboard-nickname', HTMLInputElement);
    this.submitButton = requireElement(root, '[data-submit-score]', HTMLButtonElement);
    this.status = requireElement(root, '#score-submit-status', HTMLElement);
    this.board = requireElement(root, '#leaderboard-board', HTMLElement);
    this.list = requireElement(root, '#leaderboard-list', HTMLOListElement);
    this.summary = requireElement(root, '#score-saved-summary', HTMLElement);
    this.rank = requireElement(root, '[data-global-rank]', HTMLElement);
    this.form.addEventListener('submit', this.handleSubmit);
    this.input.addEventListener('input', this.handleInput);
  }

  public begin(run: GameRunStats, nickname = ''): void {
    this.busy = false;
    this.submitted = false;
    this.hasFinishedRun = run.finishedAt !== null;
    this.currentScoreId = undefined;
    this.summary.hidden = true;
    this.rank.textContent = '--';
    this.status.textContent = '';
    this.status.dataset.status = 'idle';
    this.input.value = nickname;
    this.input.disabled = false;
    this.submitButton.textContent = 'SUBMIT SCORE';
    this.submitButton.removeAttribute('aria-busy');
    this.refreshSubmitAvailability();
    requestAnimationFrame(() => this.input.focus());
  }

  public setSubmitting(): void {
    this.busy = true;
    this.submitButton.textContent = 'SAVING...';
    this.submitButton.setAttribute('aria-busy', 'true');
    this.status.textContent = 'TRANSMITTING SCORE...';
    this.status.dataset.status = 'submitting';
    this.refreshSubmitAvailability();
  }

  public renderSubmission(outcome: SubmissionOutcome): void {
    this.busy = false;
    this.submitButton.textContent = 'SUBMIT SCORE';
    this.submitButton.removeAttribute('aria-busy');
    this.status.textContent = outcome.message;
    this.status.dataset.status = outcome.status;
    if (outcome.status === 'success' && outcome.result) {
      this.submitted = true;
      this.currentScoreId = outcome.result.scoreId;
      this.rank.textContent = `#${outcome.result.rank}`;
      this.summary.hidden = false;
      this.input.disabled = true;
      this.summary.focus();
    }
    this.refreshSubmitAvailability();
  }

  public setLeaderboardLoading(): void {
    this.list.replaceChildren(createStatusRow('LOADING TOP PILOTS...'));
    this.list.setAttribute('aria-busy', 'true');
  }

  public prepareStandaloneLeaderboard(): void {
    this.currentScoreId = undefined;
  }

  public get boardElement(): HTMLElement {
    return this.board;
  }

  public renderLeaderboard(entries: LeaderboardEntry[]): void {
    this.list.replaceChildren();
    this.list.setAttribute('aria-busy', 'false');
    if (entries.length === 0) {
      this.list.append(createStatusRow('NO SCORES YET'));
      return;
    }
    entries.forEach((entry, index) => {
      const item = document.createElement('li');
      item.classList.toggle('is-current', entry.id === this.currentScoreId);
      const rank = document.createElement('span');
      rank.textContent = String(index + 1).padStart(2, '0');
      const nickname = document.createElement('span');
      nickname.className = 'leaderboard-name';
      nickname.textContent = entry.nickname;
      const score = document.createElement('span');
      score.textContent = String(entry.score).padStart(6, '0');
      item.append(rank, nickname, score);
      this.list.append(item);
    });
  }

  public renderLeaderboardError(onRetry: () => void): void {
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.textContent = 'RETRY';
    retry.addEventListener('click', onRetry, { once: true });
    this.list.replaceChildren(createStatusRow('UNABLE TO LOAD LEADERBOARD', retry));
    this.list.setAttribute('aria-busy', 'false');
  }

  public destroy(): void {
    this.form.removeEventListener('submit', this.handleSubmit);
    this.input.removeEventListener('input', this.handleInput);
  }

  private readonly handleSubmit = (event: SubmitEvent): void => {
    event.preventDefault();
    if (this.submitButton.disabled) return;
    this.onSubmit(this.input.value);
  };

  private readonly refreshSubmitAvailability = (): void => {
    const validation = validateNickname(this.input.value);
    this.submitButton.disabled =
      !this.hasFinishedRun || this.busy || this.submitted || !validation.valid;
  };

  private readonly handleInput = (): void => {
    const validation = validateNickname(this.input.value);
    if (!this.busy && !this.submitted) {
      this.status.textContent = validation.valid ? '' : validation.message;
      this.status.dataset.status = validation.valid ? 'idle' : 'validation-error';
    }
    this.refreshSubmitAvailability();
  };
}

const createStatusRow = (message: string, action?: HTMLButtonElement): HTMLLIElement => {
  const item = document.createElement('li');
  item.className = 'leaderboard-empty';
  item.textContent = message;
  if (action) item.append(document.createElement('br'), action);
  return item;
};

const requireElement = <T extends typeof Element>(
  root: HTMLElement,
  selector: string,
  constructor: T,
): InstanceType<T> => {
  const element = root.querySelector(selector);
  if (!(element instanceof constructor))
    throw new Error(`Missing leaderboard element: ${selector}`);
  return element as InstanceType<T>;
};
